import React, { useState } from 'react';
import LandlordToolkitSidebar from './LandlordToolkitSidebar';
import { Menu } from 'lucide-react';
import { Button } from '../shadui-components/button';

interface LandlordToolkitLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string; // Added for extra context
  actions?: React.ReactNode;
  useLayout?: boolean;
}

const LandlordToolkitLayout: React.FC<LandlordToolkitLayoutProps> = ({
  children,
  title,
  subtitle,
  actions,
  useLayout = true
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  if (!useLayout) {
    return <>{children}</>;
  }

  return (
    // Use a subtle background slightly darker than white for depth
    <div className="flex min-h-screen bg-gray-50/50">
      {/* Sidebar - Assuming this component handles its own hidden/block logic for responsiveness */}
      <LandlordToolkitSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col transition-all duration-300 ease-in-out">
        {/* Unified Sticky Header */}
        <header className="sticky top-0 z-40 flex h-16 w-full items-center justify-between border-b border-gray-200 bg-white/80 px-4 backdrop-blur-md supports-[backdrop-filter]:bg-white/60 lg:px-8">
          <div className="flex items-center gap-4">
            {/* Mobile Toggle Trigger */}
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-500 hover:text-gray-900 lg:hidden"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">Open sidebar</span>
            </Button>

            {/* Breadcrumb / Title Area */}
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold leading-tight text-gray-900 lg:text-xl">
                {title || 'Dashboard'}
              </h1>
              {subtitle && (
                <p className="hidden text-xs text-gray-500 sm:block">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Actions Area */}
          <div className="flex items-center gap-3">{actions}</div>
        </header>

        {/* Scrollable Main Content */}
        <main className="flex-1 overflow-y-auto">
          {/* Constrain width for readability on ultra-wide screens */}
          <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
            {/* Optional: Add a subtle fade-in animation to children */}
            <div className="animate-in fade-in duration-500 space-y-6">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default LandlordToolkitLayout;
